"use client";

import React from "react";
import { Marquee } from "@/components/ui/marquee";
import { Star, Shield, BadgeCheck } from "lucide-react";

interface Testimonial {
  name: string;
  role: string;
  userId: string;
  content: string;
  rating: number;
}

interface JourneyTestimonialsProps {
  badge: string;
  title: string;
  reviews: Testimonial[];
}

const TestimonialCard = ({
  name,
  role,
  userId,
  content,
  rating,
}: {
  name: string;
  role: string;
  userId: string;
  content: string;
  rating: number;
}) => {
  return (
    <div className="flex w-[350px] flex-col gap-6 rounded-3xl border border-zinc-100 bg-white p-8 shadow-sm transition-shadow hover:shadow-lg">
      {/* Rating */}
      <div className="flex gap-1">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} size={16} className="fill-primary text-primary" />
        ))}
      </div>

      {/* Quote */}
      <p className="flex-1 text-base font-medium leading-relaxed text-foreground/80">
        &ldquo;{content}&rdquo;
      </p>

      {/* Author */}
      <div className="flex items-center gap-4 border-t border-zinc-100 pt-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-foreground">
          <Shield size={24} className="text-secondary" />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-bold text-foreground">{name}</span>
            <BadgeCheck size={16} className="fill-primary text-white" />
          </div>
          <span className="text-xs text-zinc-400">
            {role} · <span className="font-mono">{userId}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const JourneyTestimonials = ({ badge, title, reviews }: JourneyTestimonialsProps) => {
  const firstRow = reviews.slice(0, 3);
  const secondRow = reviews.slice(3);

  return (
    <section className="py-24 bg-muted overflow-hidden">
      <div className="flex flex-col gap-12">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-4 px-6">
          <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
            {badge}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
            {title}
          </h2>
        </div>

        {/* Testimonials Marquee */}
        <div className="flex flex-col gap-6">
          <Marquee pauseOnHover className="[--duration:40s] [--gap:1.5rem]">
            {firstRow.map((testimonial) => (
              <TestimonialCard key={testimonial.name} {...testimonial} />
            ))}
          </Marquee>
          <Marquee pauseOnHover reverse className="[--duration:40s] [--gap:1.5rem]">
            {secondRow.map((testimonial) => (
              <TestimonialCard key={testimonial.name} {...testimonial} />
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
};

export default JourneyTestimonials;
